import React from "react";
import { Button, Badge, Form } from "react-bootstrap";
import { Link } from "react-router-dom"; // Add this import
import moment from "moment";

const ProjectTicketTab = ({
  // Data
  filteredTickets,
  ticketsLoading,
  assignmentsByTicket,
  teamMemberDetails,
  resourcesByOrg,
  cusVenCpCache,
  resolvedTicketNames,
  statusMasterData,

  // Handlers
  openTicketDialog,
  handleEditTicket,
  handleConvertToTask,
  getStatusBadgeWithName,
  resolveSubStatusName,
  getPriorityBadge,
  resolveContactName,
  formatDateTime,
  formatDate,

  // Search/Filter State
  searchText,
  setSearchText,
  statusFilter,
  setStatusFilter,
  priorityFilter,
  setPriorityFilter,

  // Navigation
  navigate,
  projectId,
}) => {
  // Helper function to resolve user name from various sources
  const resolveUserName = (userId) => {
    if (!userId) return "-";

    // Check if it's a contact person (CVCP prefix)
    if (typeof userId === "string" && userId.startsWith("CVCP")) {
      return cusVenCpCache[userId] || userId;
    }

    // Check in team members
    const teamMember = teamMemberDetails.find(
      (user) => String(user.userId) === String(userId),
    );
    if (teamMember) return teamMember.username;

    // Check in resources by org
    const resource = resourcesByOrg.find(
      (res) => String(res.resourceId) === String(userId),
    );
    if (resource) return resource.name;

    return userId;
  };

  return (
    <div className="project-view-fade-in">
      <div className="project-view-ticket-list-container">
        <div className="ticket-list-header">
          <h4 className="ticket-list-title">Project Tickets</h4>
          <div
            className="ticket-list-filters d-flex gap-2 flex-wrap"
            style={{
              display: "flex",
              flexDirection: "row",
              width: "50vw",
            }}
          >
            <span>
              <input
                type="text"
                className="form-control"
                placeholder="Search ticket name or description..."
                style={{ maxWidth: "250px" }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </span>
            <span style={{ width: "10vw" }}>
              <select
                className="form-select"
                style={{ maxWidth: "180px" }}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="Resolved">Resolved</option>
                <option value="Closed">Closed</option>
              </select>
            </span>
            <span style={{ width: "10vw" }}>
              <select
                className="form-select"
                style={{ maxWidth: "180px" }}
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
              >
                <option value="">All Priority</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </span>

            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setSearchText("");
                setStatusFilter("");
                setPriorityFilter("");
              }}
            >
              Clear
            </Button>
          </div>
        </div>

        {ticketsLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading tickets...</span>
            </div>
          </div>
        ) : (
          <div className="ticket-table-container">
            {filteredTickets.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-ticket-alt fa-3x mb-3"></i>
                <h5>No Tickets Found</h5>
                <p className="mb-4">
                  There are no tickets associated with this project.
                </p>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => navigate(`/addTicket?projectId=${projectId}`)}
                >
                  <i className="fas fa-plus me-2"></i>
                  Create First Ticket
                </Button>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="project-view-ticket-table">
                  <thead className="project-view-ticket-table-header">
                    <tr>
                      <th>Ticket ID</th>
                      <th>Ticket Short ID</th>
                      <th>Ticket Name</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th>Sub Status</th>
                      <th>Ticket Type</th>
                      <th>Priority</th>
                      <th>Submitted By</th>
                      <th>Created By</th>
                      <th>Assigned To</th>
                      <th>Category</th>
                      <th>Ticket Reason</th>
                      <th>Created Date</th>
                      <th>Due Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="ticket-table-body">
                    {filteredTickets.map((ticket) => {
                      return (
                        <tr key={ticket.ticketId} className="ticket-table-row">
                          <td className="ticket-id-cell">
                            <div className="ticket-id">{ticket.ticketId}</div>
                          </td>
                          <td className="ticket-short-id-cell">
                            <div className="ticket-short-id">
                              {ticket.ticketShortId}
                            </div>
                          </td>
                          <td className="ticket-name-cell">
                            {/* Updated: Make ticket name clickable with Link */}
                            <Link
                              to={`/ticketinfo/${ticket.ticketId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ticket-name-link"
                              style={{
                                color: "#0d6efd",
                                textDecoration: "none",
                                fontWeight: "500",
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.textDecoration =
                                  "underline";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.textDecoration = "none";
                              }}
                              title="Open ticket details (opens in new tab)"
                            >
                              {ticket.ticketName}
                            </Link>
                          </td>
                          <td className="ticket-description-cell">
                            <div className="ticket-description">
                              {ticket.description
                                ? ticket.description.length > 100
                                  ? `${ticket.description.substring(0, 100)}...`
                                  : ticket.description
                                : "-"}
                            </div>
                          </td>
                          <td className="ticket-status-cell">
                            {getStatusBadgeWithName(ticket.status)}
                          </td>
                          <td className="ticket-substatus-cell">
                            <Badge bg="secondary">
                              {resolveSubStatusName(ticket.subStatus)}
                            </Badge>
                          </td>
                          <td className="ticket-type-cell">
                            <Badge bg="outline-info" style={{ color: "black" }}>
                              {resolvedTicketNames.types[
                                ticket.ticketType?.toLowerCase()
                              ] ||
                                ticket.ticketType?.substring(0, 8) ||
                                "N/A"}
                            </Badge>
                          </td>
                          <td className="ticket-priority-cell">
                            <span
                              className={`project-view-ticket-priority-badge ${getPriorityBadge(
                                ticket.priority,
                              )}`}
                            >
                              {ticket.priority}
                            </span>
                          </td>

                          {/* Submitted By Column */}
                          <td className="ticket-submittedby-cell">
                            <div className="ticket-submittedby">
                              {resolveContactName(
                                ticket.submittedBy ||
                                  ticket.reportedBy ||
                                  ticket.customerId ||
                                  "-",
                              )}
                            </div>
                          </td>

                          {/* Created By Column */}
                          <td className="ticket-createdby-cell">
                            <div className="ticket-createdby">
                              {resolveUserName(ticket.createdBy)}
                            </div>
                          </td>

                          {/* Assigned To Column */}
                          <td className="ticket-assignedto-cell">
                            <div className="ticket-assignedto d-flex flex-column gap-1">
                              {(() => {
                                const currentAssignments =
                                  assignmentsByTicket[ticket.ticketId] || [];

                                if (currentAssignments.length === 0) {
                                  return (
                                    <div className="d-flex align-items-center justify-content-between">
                                      <span className="text-muted">
                                        Unassigned
                                      </span>
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => openTicketDialog(ticket)}
                                        className="ms-2"
                                        title="Assign user"
                                      >
                                        Assign
                                      </Button>
                                    </div>
                                  );
                                }

                                return (
                                  <>
                                    {currentAssignments.map(
                                      (assignment, index) => {
                                        const userName = resolveUserName(
                                          assignment.assignedUserId,
                                        );

                                        return (
                                          <div
                                            key={
                                              assignment.assignmentId || index
                                            }
                                            className="d-flex align-items-center justify-content-between"
                                          >
                                            <div className="d-flex align-items-center">
                                              <span className="me-2">
                                                {userName}
                                              </span>
                                              <Badge
                                                bg={
                                                  assignment.isActive
                                                    ? "success"
                                                    : "secondary"
                                                }
                                                className="assignment-status-badge"
                                              >
                                                {assignment.isActive
                                                  ? "Active"
                                                  : "Completed"}
                                              </Badge>
                                              {assignment.totalHours && (
                                                <small className="ms-2 text-muted">
                                                  (
                                                  {parseFloat(
                                                    assignment.totalHours,
                                                  ).toFixed(2)}
                                                  h)
                                                </small>
                                              )}
                                            </div>

                                            {index === 0 && (
                                              <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() =>
                                                  openTicketDialog(ticket)
                                                }
                                                className="ms-2"
                                                title="Manage Assignments"
                                              >
                                                Manage
                                              </Button>
                                            )}
                                          </div>
                                        );
                                      },
                                    )}

                                    {currentAssignments.length > 2 && (
                                      <small className="text-muted">
                                        +{currentAssignments.length - 2} more
                                        assignments
                                      </small>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </td>

                          <td className="ticket-category-cell">
                            <Badge bg="info">
                              {resolvedTicketNames.categories[
                                ticket.category?.toLowerCase()
                              ] ||
                                ticket.category?.substring(0, 8) ||
                                "N/A"}
                            </Badge>
                          </td>
                          <td className="ticket-reason-cell">
                            <div className="ticket-reason">
                              {resolvedTicketNames.reasons[
                                ticket.ticketReason?.toLowerCase()
                              ] ||
                                ticket.ticketReason ||
                                "N/A"}
                            </div>
                          </td>
                          <td className="ticket-date-cell">
                            {formatDateTime(ticket.ticketTime)}
                          </td>
                          <td className="ticket-date-cell">
                            {formatDate(ticket.dueDate)}
                          </td>
                          <td className="ticket-actions-cell">
                            <div
                              className="ticket-actions"
                              style={{ display: "flex" }}
                            >
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                className="me-1"
                                onClick={() => openTicketDialog(ticket)}
                                title="Open Ticket Details"
                              >
                                Open
                              </Button>

                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleEditTicket(ticket)}
                                title="Edit Ticket"
                                className="me-1"
                              >
                                Edit
                              </Button>

                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleConvertToTask(ticket)}
                                title="Convert to Task"
                                className="me-1"
                              >
                                Convert to Task
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectTicketTab;

// import React from "react";
// import { Button, Badge, Form } from "react-bootstrap";
// import moment from "moment";

// const ProjectTicketTab = ({
//   // Data
//   filteredTickets,
//   ticketsLoading,
//   assignmentsByTicket,
//   teamMemberDetails,
//   resourcesByOrg,
//   cusVenCpCache,
//   resolvedTicketNames,
//   statusMasterData,

//   // Handlers
//   openTicketDialog,
//   handleEditTicket,
//   handleConvertToTask,
//   getStatusBadgeWithName,
//   resolveSubStatusName,
//   getPriorityBadge,
//   resolveContactName,
//   formatDateTime,
//   formatDate,

//   // Search/Filter State
//   searchText,
//   setSearchText,
//   statusFilter,
//   setStatusFilter,
//   priorityFilter,
//   setPriorityFilter,

//   // Navigation
//   navigate,
//   projectId,
// }) => {
//   // Helper function to resolve user name from various sources
//   const resolveUserName = (userId) => {
//     if (!userId) return "-";

//     // Check if it's a contact person (CVCP prefix)
//     if (typeof userId === "string" && userId.startsWith("CVCP")) {
//       return cusVenCpCache[userId] || userId;
//     }

//     // Check in team members
//     const teamMember = teamMemberDetails.find(
//       (user) => String(user.userId) === String(userId),
//     );
//     if (teamMember) return teamMember.username;

//     // Check in resources by org
//     const resource = resourcesByOrg.find(
//       (res) => String(res.resourceId) === String(userId),
//     );
//     if (resource) return resource.name;

//     return userId;
//   };

//   return (
//     <div className="project-view-fade-in">
//       <div className="project-view-ticket-list-container">
//         <div className="ticket-list-header">
//           <h4 className="ticket-list-title">Project Tickets</h4>
//           <div
//             className="ticket-list-filters d-flex gap-2 flex-wrap"
//             style={{
//               display: "flex",
//               flexDirection: "row",
//               width: "50vw",
//             }}
//           >
//             <span>
//               <input
//                 type="text"
//                 className="form-control"
//                 placeholder="Search ticket name or description..."
//                 style={{ maxWidth: "250px" }}
//                 value={searchText}
//                 onChange={(e) => setSearchText(e.target.value)}
//               />
//             </span>
//             <span style={{ width: "10vw" }}>
//               <select
//                 className="form-select"
//                 style={{ maxWidth: "180px" }}
//                 value={statusFilter}
//                 onChange={(e) => setStatusFilter(e.target.value)}
//               >
//                 <option value="">All Status</option>
//                 <option value="Open">Open</option>
//                 <option value="In Progress">In Progress</option>
//                 <option value="Resolved">Resolved</option>
//                 <option value="Closed">Closed</option>
//               </select>
//             </span>
//             <span style={{ width: "10vw" }}>
//               <select
//                 className="form-select"
//                 style={{ maxWidth: "180px" }}
//                 value={priorityFilter}
//                 onChange={(e) => setPriorityFilter(e.target.value)}
//               >
//                 <option value="">All Priority</option>
//                 <option value="Low">Low</option>
//                 <option value="Medium">Medium</option>
//                 <option value="High">High</option>
//                 <option value="Critical">Critical</option>
//               </select>
//             </span>

//             <Button
//               variant="outline-secondary"
//               size="sm"
//               onClick={() => {
//                 setSearchText("");
//                 setStatusFilter("");
//                 setPriorityFilter("");
//               }}
//             >
//               Clear
//             </Button>
//           </div>
//         </div>

//         {ticketsLoading ? (
//           <div className="text-center py-4">
//             <div className="spinner-border text-primary" role="status">
//               <span className="visually-hidden">Loading tickets...</span>
//             </div>
//           </div>
//         ) : (
//           <div className="ticket-table-container">
//             {filteredTickets.length === 0 ? (
//               <div className="text-center py-5 text-muted">
//                 <i className="fas fa-ticket-alt fa-3x mb-3"></i>
//                 <h5>No Tickets Found</h5>
//                 <p className="mb-4">
//                   There are no tickets associated with this project.
//                 </p>
//                 <Button
//                   variant="outline-primary"
//                   size="sm"
//                   onClick={() => navigate(`/addTicket?projectId=${projectId}`)}
//                 >
//                   <i className="fas fa-plus me-2"></i>
//                   Create First Ticket
//                 </Button>
//               </div>
//             ) : (
//               <div className="table-responsive">
//                 <table className="project-view-ticket-table">
//                   <thead className="project-view-ticket-table-header">
//                     <tr>
//                       <th>Ticket ID</th>
//                       <th>Ticket Short ID</th>
//                       <th>Ticket Name</th>
//                       <th>Description</th>
//                       <th>Status</th>
//                       <th>Sub Status</th>
//                       <th>Ticket Type</th>
//                       <th>Priority</th>
//                       <th>Submitted By</th>
//                       <th>Created By</th>
//                       <th>Assigned To</th>
//                       <th>Category</th>
//                       <th>Ticket Reason</th>
//                       <th>Created Date</th>
//                       <th>Due Date</th>
//                       <th>Actions</th>
//                     </tr>
//                   </thead>
//                   <tbody className="ticket-table-body">
//                     {filteredTickets.map((ticket) => {
//                       return (
//                         <tr key={ticket.ticketId} className="ticket-table-row">
//                           <td className="ticket-id-cell">
//                             <div className="ticket-id">{ticket.ticketId}</div>
//                           </td>
//                           <td className="ticket-short-id-cell">
//                             <div className="ticket-short-id">
//                               {ticket.ticketShortId}
//                             </div>
//                           </td>
//                           <td className="ticket-name-cell">
//                             <div className="ticket-name">
//                               {ticket.ticketName}
//                             </div>
//                           </td>
//                           <td className="ticket-description-cell">
//                             <div className="ticket-description">
//                               {ticket.description
//                                 ? ticket.description.length > 100
//                                   ? `${ticket.description.substring(0, 100)}...`
//                                   : ticket.description
//                                 : "-"}
//                             </div>
//                           </td>
//                           <td className="ticket-status-cell">
//                             {getStatusBadgeWithName(ticket.status)}
//                           </td>
//                           <td className="ticket-substatus-cell">
//                             <Badge bg="secondary">
//                               {resolveSubStatusName(ticket.subStatus)}
//                             </Badge>
//                           </td>
//                           <td className="ticket-type-cell">
//                             <Badge bg="outline-info" style={{ color: "black" }}>
//                               {resolvedTicketNames.types[
//                                 ticket.ticketType?.toLowerCase()
//                               ] ||
//                                 ticket.ticketType?.substring(0, 8) ||
//                                 "N/A"}
//                             </Badge>
//                           </td>
//                           <td className="ticket-priority-cell">
//                             <span
//                               className={`project-view-ticket-priority-badge ${getPriorityBadge(
//                                 ticket.priority,
//                               )}`}
//                             >
//                               {ticket.priority}
//                             </span>
//                           </td>

//                           {/* Submitted By Column */}
//                           <td className="ticket-submittedby-cell">
//                             <div className="ticket-submittedby">
//                               {resolveContactName(
//                                 ticket.submittedBy ||
//                                   ticket.reportedBy ||
//                                   ticket.customerId ||
//                                   "-",
//                               )}
//                             </div>
//                           </td>

//                           {/* Created By Column */}
//                           <td className="ticket-createdby-cell">
//                             <div className="ticket-createdby">
//                               {resolveUserName(ticket.createdBy)}
//                             </div>
//                           </td>

//                           {/* Assigned To Column */}
//                           <td className="ticket-assignedto-cell">
//                             <div className="ticket-assignedto d-flex flex-column gap-1">
//                               {(() => {
//                                 const currentAssignments =
//                                   assignmentsByTicket[ticket.ticketId] || [];

//                                 if (currentAssignments.length === 0) {
//                                   return (
//                                     <div className="d-flex align-items-center justify-content-between">
//                                       <span className="text-muted">
//                                         Unassigned
//                                       </span>
//                                       <Button
//                                         variant="outline-primary"
//                                         size="sm"
//                                         onClick={() => openTicketDialog(ticket)}
//                                         className="ms-2"
//                                         title="Assign user"
//                                       >
//                                         Assign
//                                       </Button>
//                                     </div>
//                                   );
//                                 }

//                                 return (
//                                   <>
//                                     {currentAssignments.map(
//                                       (assignment, index) => {
//                                         const userName = resolveUserName(
//                                           assignment.assignedUserId,
//                                         );

//                                         return (
//                                           <div
//                                             key={
//                                               assignment.assignmentId || index
//                                             }
//                                             className="d-flex align-items-center justify-content-between"
//                                           >
//                                             <div className="d-flex align-items-center">
//                                               <span className="me-2">
//                                                 {userName}
//                                               </span>
//                                               <Badge
//                                                 bg={
//                                                   assignment.isActive
//                                                     ? "success"
//                                                     : "secondary"
//                                                 }
//                                                 className="assignment-status-badge"
//                                               >
//                                                 {assignment.isActive
//                                                   ? "Active"
//                                                   : "Completed"}
//                                               </Badge>
//                                               {assignment.totalHours && (
//                                                 <small className="ms-2 text-muted">
//                                                   (
//                                                   {parseFloat(
//                                                     assignment.totalHours,
//                                                   ).toFixed(2)}
//                                                   h)
//                                                 </small>
//                                               )}
//                                             </div>

//                                             {index === 0 && (
//                                               <Button
//                                                 variant="outline-primary"
//                                                 size="sm"
//                                                 onClick={() =>
//                                                   openTicketDialog(ticket)
//                                                 }
//                                                 className="ms-2"
//                                                 title="Manage Assignments"
//                                               >
//                                                 Manage
//                                               </Button>
//                                             )}
//                                           </div>
//                                         );
//                                       },
//                                     )}

//                                     {currentAssignments.length > 2 && (
//                                       <small className="text-muted">
//                                         +{currentAssignments.length - 2} more
//                                         assignments
//                                       </small>
//                                     )}
//                                   </>
//                                 );
//                               })()}
//                             </div>
//                           </td>

//                           <td className="ticket-category-cell">
//                             <Badge bg="info">
//                               {resolvedTicketNames.categories[
//                                 ticket.category?.toLowerCase()
//                               ] ||
//                                 ticket.category?.substring(0, 8) ||
//                                 "N/A"}
//                             </Badge>
//                           </td>
//                           <td className="ticket-reason-cell">
//                             <div className="ticket-reason">
//                               {resolvedTicketNames.reasons[
//                                 ticket.ticketReason?.toLowerCase()
//                               ] ||
//                                 ticket.ticketReason ||
//                                 "N/A"}
//                             </div>
//                           </td>
//                           <td className="ticket-date-cell">
//                             {formatDateTime(ticket.ticketTime)}
//                           </td>
//                           <td className="ticket-date-cell">
//                             {formatDate(ticket.dueDate)}
//                           </td>
//                           <td className="ticket-actions-cell">
//                             <div
//                               className="ticket-actions"
//                               style={{ display: "flex" }}
//                             >
//                               <Button
//                                 variant="outline-secondary"
//                                 size="sm"
//                                 className="me-1"
//                                 onClick={() => openTicketDialog(ticket)}
//                                 title="Open Ticket Details"
//                               >
//                                 Open
//                               </Button>

//                               <Button
//                                 variant="outline-primary"
//                                 size="sm"
//                                 onClick={() => handleEditTicket(ticket)}
//                                 title="Edit Ticket"
//                                 className="me-1"
//                               >
//                                 Edit
//                               </Button>

//                               <Button
//                                 variant="outline-primary"
//                                 size="sm"
//                                 onClick={() => handleConvertToTask(ticket)}
//                                 title="Convert to Task"
//                                 className="me-1"
//                               >
//                                 Convert to Task
//                               </Button>
//                             </div>
//                           </td>
//                         </tr>
//                       );
//                     })}
//                   </tbody>
//                 </table>
//               </div>
//             )}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ProjectTicketTab;
